import { useMutation } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetCommissionStatementDocumentMutationOptions,
  type CommissionStatementAnnualDeclarationDto,
  commissionStatementDocumentTypeEnum,
} from 'korint-sdk';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { formatAmount } from '../formatAmount';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function AnnualDeclarationRow({
  brokerageFirmId,
  declaration,
}: {
  brokerageFirmId: string;
  declaration: CommissionStatementAnnualDeclarationDto;
}) {
  const { t } = useTranslation();
  const { mutate, isPending, isError } = useMutation(
    brokerageFirmControllerGetCommissionStatementDocumentMutationOptions(),
  );

  // Opened within the click so the browser does not block it; the PDF loads once rendered.
  const openDeclaration = () => {
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    mutate(
      {
        brokerageFirmId,
        data: {
          type: commissionStatementDocumentTypeEnum.ANNUAL_DECLARATION,
          period: String(declaration.year),
        },
      },
      {
        onSuccess: ({ signedUrl }) => tab?.location.assign(signedUrl),
        onError: () => tab?.close(),
      },
    );
  };

  return (
    <div className="py-3">
      <Group justify="between">
        <span
          className={`flex items-center gap-2 ${declaration.available ? '' : 'text-muted-foreground'}`}
        >
          <FileText className="size-4" />
          {t('commissions.annual.exercise', { year: declaration.year })}
        </span>
        {declaration.available ? (
          <Group>
            <Text inline weight="semibold">
              {formatAmount(declaration.totalCents)}
            </Text>
            <Button variant="outline" size="sm" disabled={isPending} onClick={openDeclaration}>
              {isPending ? (
                <Loader2 className="mr-1 size-4 animate-spin" />
              ) : (
                <Download className="mr-1 size-4" />
              )}
              PDF
            </Button>
          </Group>
        ) : (
          <Text inline size="sm" tone="muted">
            {t('commissions.annual.availableAfterClosing', { year: declaration.year })}
          </Text>
        )}
      </Group>
      {isError && (
        <Text size="sm" tone="destructive" className="mt-2 text-right">
          {t('commissions.annual.error')}
        </Text>
      )}
    </div>
  );
}
